import React from 'react';
import { AlertTriangle, X } from 'lucide-react';

const RedactionApplyModal = ({ isOpen, onClose, onConfirm, marks, loading }) => {
  if (!isOpen) return null;

  const confirmedMarks = marks.filter((m) => m.is_confirmed);

  // Count by reason code
  const reasonBreakdown = {};
  confirmedMarks.forEach((m) => {
    const reason = m.reason_code || 'No reason';
    reasonBreakdown[reason] = (reasonBreakdown[reason] || 0) + 1;
  });

  // Count by page
  const pageCount = new Set(confirmedMarks.map((m) => m.page_number)).size;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Apply Redactions</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-semibold mb-1">This action is irreversible</p>
              <p>
                Redacted content will be permanently removed from the document.
                The original document will not be modified — a new redacted copy will be created.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total redactions:</span>
              <span className="font-medium">{confirmedMarks.length}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Pages affected:</span>
              <span className="font-medium">{pageCount}</span>
            </div>
          </div>

          {Object.keys(reasonBreakdown).length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-1">By reason:</h4>
              <div className="space-y-1">
                {Object.entries(reasonBreakdown).map(([reason, count]) => (
                  <div key={reason} className="flex justify-between text-sm">
                    <span className="text-gray-600">{reason.replace(/_/g, ' ')}</span>
                    <span className="text-gray-900">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200 rounded"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading || confirmedMarks.length === 0}
            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
          >
            {loading ? 'Applying...' : `Apply ${confirmedMarks.length} Redaction${confirmedMarks.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RedactionApplyModal;
